"use client";

import * as React from "react";
import type { ImageCropRectNorm } from "../../../kernel/nodes/imageBlock.types";

function isFullFrame(c: ImageCropRectNorm | null | undefined): boolean {
  if (!c) return true;
  return c.x <= 1e-4 && c.y <= 1e-4 && c.w >= 1 - 1e-4 && c.h >= 1 - 1e-4;
}

/**
 * 按归一化裁切框展示原图：保持 `src` 不变，用缩放 + 平移 + overflow:hidden 实现「视窗 cover」。
 */
export function CroppedImageView({
  src,
  alt,
  cropNorm,
}: {
  src: string;
  alt: string;
  cropNorm: ImageCropRectNorm | null;
}) {
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const imgRef = React.useRef<HTMLImageElement | null>(null);
  const [natural, setNatural] = React.useState<{ nw: number; nh: number } | null>(null);
  const [layoutReady, setLayoutReady] = React.useState(false);

  React.useEffect(() => {
    setLayoutReady(false);
  }, [src, cropNorm]);

  const applyLayout = React.useCallback(() => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img || !natural || !cropNorm || isFullFrame(cropNorm)) return;
    const { nw, nh } = natural;
    const cw = Math.max(1, cropNorm.w * nw);
    const ch = Math.max(1, cropNorm.h * nh);
    const W = wrap.clientWidth;
    if (W <= 0) return;
    const s = W / cw;
    img.style.width = `${nw * s}px`;
    img.style.height = `${nh * s}px`;
    img.style.left = `${-cropNorm.x * nw * s}px`;
    img.style.top = `${-cropNorm.y * nh * s}px`;
    setLayoutReady(true);
  }, [natural, cropNorm]);

  React.useLayoutEffect(() => {
    applyLayout();
  }, [applyLayout]);

  React.useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => applyLayout());
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [applyLayout]);

  const onImgLoad = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    if (el.naturalWidth > 0) setNatural({ nw: el.naturalWidth, nh: el.naturalHeight });
  }, []);

  if (!cropNorm || isFullFrame(cropNorm)) {
    return (
      <img
        src={src}
        alt={alt}
        onLoad={onImgLoad}
        className="w-full h-auto object-contain rounded-md select-none"
        draggable={false}
      />
    );
  }

  const ar = natural ? (cropNorm.w * natural.nw) / (cropNorm.h * natural.nh) : cropNorm.w / cropNorm.h;

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden rounded-md bg-default-100/80"
      style={{ aspectRatio: ar }}
    >
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        onLoad={onImgLoad}
        draggable={false}
        className="absolute max-w-none select-none"
        style={{
          opacity: layoutReady ? 1 : 0,
        }}
      />
    </div>
  );
}
